import { useSyncExternalStore, useCallback, useEffect, useRef } from 'react';
import { createAuthStore } from '@caro/shared/auth';
import { createApiClient } from '@caro/shared/api';
import { API_ENDPOINTS } from '@caro/shared/constants';
import { cleanupSip } from './useSip';
import * as MqttTelemetry from '../services/MqttTelemetry';

const STORAGE_KEY = 'caro_auth';

// Singleton auth store for the resident app
const authStore = createAuthStore();

// ── LocalStorage persistence ────────────────────────────────────
function saveToStorage(state: { accessToken: string | null; refreshToken: string | null; user: any }) {
  try {
    if (state.accessToken) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        user: state.user,
      }));
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
  } catch {
    // localStorage not available (SSR, private browsing)
  }
}

function loadFromStorage(): { accessToken: string; refreshToken: string; user: any } | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

// Restore session from localStorage on startup
const saved = loadFromStorage();
if (saved?.accessToken) {
  authStore.getState().setAccessToken(saved.accessToken);
  if (saved.refreshToken) {
    authStore.getState().setRefreshToken(saved.refreshToken);
  }
  if (saved.user) {
    authStore.getState().setUser(saved.user);
  }
}

// Subscribe to store changes → persist to localStorage
authStore.subscribe((state) => {
  saveToStorage({
    accessToken: state.accessToken,
    refreshToken: state.refreshToken,
    user: state.user,
  });
});

// ── API client ──────────────────────────────────────────────────
const apiBaseUrl = (import.meta as any).env?.VITE_API_URL || 'https://api.caro.care';

// Circuit breaker: prevent spamming refresh endpoint after failures
let lastRefreshFailure = 0;
const REFRESH_COOLDOWN_MS = 15_000; // 15s cooldown after a failed refresh

const api = createApiClient({
  baseURL: apiBaseUrl,
  getAccessToken: () => authStore.getState().accessToken,
  onTokenExpired: async () => {
    // If we recently failed a refresh, don't retry yet
    if (Date.now() - lastRefreshFailure < REFRESH_COOLDOWN_MS) {
      return null;
    }

    try {
      const refreshToken = authStore.getState().refreshToken;
      if (!refreshToken) return null;

      const res = await fetch(`${apiBaseUrl}${API_ENDPOINTS.AUTH_REFRESH}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
      });
      if (res.ok) {
        const data = await res.json();
        lastRefreshFailure = 0; // Reset on success
        authStore.getState().setAccessToken(data.token);
        if (data.refreshToken) {
          authStore.getState().setRefreshToken(data.refreshToken);
        }
        return data.token;
      }
      // Refresh failed (429/401/etc) — activate cooldown
      lastRefreshFailure = Date.now();
    } catch {
      lastRefreshFailure = Date.now();
    }
    return null;
  },
  onAuthFailed: () => {
    // Clean up SIP/MQTT before clearing auth (components will unmount
    // and their effects won't re-run with the new isAuthenticated value)
    cleanupSip();
    MqttTelemetry.disconnect();
    authStore.getState().logout();
    localStorage.removeItem(STORAGE_KEY);
  },
});

// ── Hook ────────────────────────────────────────────────────────
export function useAuth() {
  const state = useSyncExternalStore(
    authStore.subscribe,
    () => authStore.getState(),
  );

  // On mount, if we have a token but no fresh user data, fetch /me
  const hasCheckedRef = useRef(false);
  useEffect(() => {
    if (hasCheckedRef.current) return;
    if (!state.accessToken || state.user) return;
    hasCheckedRef.current = true;

    // We have a token from localStorage but user might be stale — validate it
    api.get<{ success: boolean; user: any }>(API_ENDPOINTS.AUTH_ME)
      .then((data) => {
        if (data.user) {
          state.setUser(data.user);
        }
      })
      .catch(() => {
        // Token invalid — clear everything
        state.logout();
      });
  }, [state]);

  const login = useCallback(async (email: string, password: string): Promise<{ activeDeviceCount: number }> => {
    state.setLoading(true);
    try {
      const data = await api.post<{
        user: any;
        person?: any;
        accessToken: string;
        refreshToken: string;
        activeDeviceCount?: number;
      }>(
        API_ENDPOINTS.AUTH_LOGIN,
        { email, password, app: 'resident' },
      );
      state.setAccessToken(data.accessToken);
      if (data.refreshToken) {
        state.setRefreshToken(data.refreshToken);
      }
      // Merge person data into the user object for the resident app
      const person = data.person || null;
      const userWithPerson = {
        ...data.user,
        person,
        firstName: person?.firstName || data.user.username,
        lastName: person?.lastName || '',
        locationId: person?.location?.id,
        locationName: person?.location?.name,
        emergencyCountdownSeconds: person?.emergencyCountdownSeconds,
        autoAnswerVideophone: person?.autoAnswerVideophone,
        isResidentAppEnabled: person?.isResidentAppEnabled,
      };
      state.setUser(userWithPerson);
      return { activeDeviceCount: data.activeDeviceCount || 1 };
    } catch (err: any) {
      state.setError(err.response?.data?.message || 'Login failed');
      throw err;
    }
  }, [state]);

  const logout = useCallback(async () => {
    // 1. Disconnect SIP and MQTT immediately (before components unmount —
    //    React effects won't re-run with new deps when a component unmounts)
    cleanupSip();
    MqttTelemetry.disconnect();

    // 2. Revoke refresh token on server so it can't be reused
    try {
      await api.post(API_ENDPOINTS.AUTH_LOGOUT, {
        refreshToken: state.refreshToken,
      });
    } catch {
      // Logout API call may fail, but we still clear local state
    }

    // 3. Reset token refresh cooldown so next login isn't blocked
    lastRefreshFailure = 0;

    // 4. Clear auth state (triggers subscribe → removes localStorage)
    state.logout();
    localStorage.removeItem(STORAGE_KEY);
  }, [state]);

  return {
    user: state.user,
    isAuthenticated: state.isAuthenticated,
    isLoading: state.isLoading,
    error: state.error,
    accessToken: state.accessToken,
    refreshToken: state.refreshToken,
    login,
    logout,
    api,
  };
}

export { api };
